/**
 * 创建评估 Hook
 * 包含乐观更新 (Optimistic Updates) 和错误处理逻辑
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import assessmentService, {
  CreateAssessmentRequest,
  CreateAssessmentResponse,
} from "@/services/assessmentService";
import { PointsBalance } from "@/services/pointsService";
import { ApiError } from "@/types/apiErrors";

interface UseCreateAssessmentOptions {
  onSuccess?: (data: CreateAssessmentResponse) => void;
  onError?: (error: Error) => void;
}

export function useCreateAssessment(options: UseCreateAssessmentOptions = {}) {
  const { t } = useTranslation("assessments");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAssessmentRequest) =>
      assessmentService.createAssessment(data),

    // 乐观更新
    onMutate: async (newData) => {
      // 1. 取消正在进行的查询，避免冲突
      await queryClient.cancelQueries({ queryKey: ["points", "balance"] });

      // 2. 获取之前的状态快照 (用于回滚)
      const previousPoints = queryClient.getQueryData<PointsBalance>([
        "points",
        "balance",
      ]);

      // 3. 乐观更新 UI
      if (previousPoints) {
        // 假设一次消耗 100 积分 (这里暂时写死，理想情况应该从配置或前端常量获取)
        // 或者是从 newData 中某种方式获取预估消耗
        const ESTIMATED_COST = 100;

        queryClient.setQueryData<PointsBalance>(
          ["points", "balance"],
          (old) => {
            if (!old) return old;
            return {
              ...old,
              balance: old.balance - ESTIMATED_COST,
              frozen_balance: old.frozen_balance + ESTIMATED_COST,
              // total_balance 不变
              total_balance: old.total_balance,
            };
          },
        );
      }

      // 返回快照
      return { previousPoints };
    },

    onError: (err, newData, context) => {
      // 1. 回滚到之前的状态
      if (context?.previousPoints) {
        queryClient.setQueryData(["points", "balance"], context.previousPoints);
      }

      // 2. 显示错误提示
      if (err instanceof ApiError && err.status === 402) {
        // 积分不足错误由前端专门 UI 处理 (Dialog)，这里可能不需要 toast，或者显示特定 toast
        // 但为了保险起见，或者如果是网络错误导致的，需要提示
      } else {
        toast.error(
          t("errors.createFailedAndRolledBack", "请求失败，积分未扣除"),
        );
      }

      options.onError?.(err as Error);
    },

    onSuccess: (data) => {
      // 1. 刷新积分数据 (确保与服务器同步)
      queryClient.invalidateQueries({ queryKey: ["points", "balance"] });
      // 2. 刷新评估历史
      queryClient.invalidateQueries({ queryKey: ["assessment-history"] });

      options.onSuccess?.(data);
    },
  });
}
